import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { SvgIcon } from 'src/components/icons/svg-icon'
import { CLASS_PREFIX } from 'src/constants/'
import { FRONTEND_CREATE_PROFILE, FRONTEND_NEWSFEED_PATH } from 'src/constants/urls'
import { useAuthContext } from 'src/context/authContext'
import { textResources } from 'src/lang/de'
import { UrlUtils } from 'src/utils/url/url'
import { EmailVerification } from '../layouts/email_verification/email_verification'
import { ForgotPasswordForm } from '../layouts/forgot_password_form/forgot_password_form'
import { InitialAuthUI } from '../layouts/initial/initial_auth_ui'
import { LoginForm } from '../layouts/initial/login_form'
import { PhoneCodeVerification } from '../layouts/phone_code_verification/phone_code_verification'
import { PhoneVerification } from '../layouts/phone_verification/phone_verification'
import { PublicProfileInfo } from '../layouts/public_profile_info/public_profile_info'
import { RegistrationForm } from '../layouts/registration_form/registration_form'
import { ResetPasswordForm } from '../layouts/reset_password_form/reset_password_form'
import { SignupComplete } from '../layouts/signup_complete/signup_complete'
import { AuthenticationModal } from '../modal/authentication_modal'
import './auth.scss'

type IView = 'initial' | 'registration-form' | 'email-verification' | 'phone-verification' | 'phone-code-verification' | 'forgot-password' | 'reset-password' | 'public-profile-info' | 'public-profile-login' | 'signup-complete'

type AuthMode = 'full' | 'login-only'

type Props = {
  closeModal: () => void
  mode?: AuthMode
  initialView?: IView
  initialTab?: 'login' | 'register'
  closeable?: boolean
  resetToken?: string
  resetEmail?: string
  initialEmail?: string
  initialPhoneNumber?: string
  isPublicProfileFlow?: boolean
}

export const Auth: React.FC<Props> = ({
  closeModal,
  mode = 'full',
  initialView = 'initial',
  initialTab = 'login',
  closeable = true,
  resetToken,
  resetEmail,
  initialEmail = '',
  initialPhoneNumber = '',
  isPublicProfileFlow: initialIsPublicProfileFlow = false
}) => {
  const [activeView, setActiveView] = useState<IView>(initialView)
  // This host has no login view of its own — login lives inside InitialAuthUI, which
  // seeds its tab from a prop on mount. Coming back from a failed sign-in therefore
  // has to say which tab it means, instead of falling back to the caller's choice.
  const [returnToLogin, setReturnToLogin] = useState(false)
  const [loginEmail, setLoginEmail] = useState('')
  const [phoneNumber, setPhoneNumber] = useState(initialPhoneNumber)
  const [email, setEmail] = useState(initialEmail)
  const [isPublicProfileFlow, setIsPublicProfileFlow] = useState(initialIsPublicProfileFlow)
  const pendingProfileIdRef = useRef<string | undefined>()
  const isMountedRef = useRef(true)
  const history = useHistory()
  const { setSelectedProfileId } = useAuthContext()

  useEffect(() => {
    isMountedRef.current = true
    return () => { isMountedRef.current = false }
  }, [])

  const commitPendingProfileId = useCallback(() => {
    if (pendingProfileIdRef.current) {
      setSelectedProfileId(pendingProfileIdRef.current)
    }
  }, [setSelectedProfileId])

  const storePendingProfileId = useCallback((profileId?: string) => {
    pendingProfileIdRef.current = profileId
  }, [])

  const wrappedCloseModal = useCallback(() => {
    if (!closeable) return
    commitPendingProfileId()
    closeModal()
  }, [closeable, closeModal, commitPendingProfileId])
  const publicProfileEmailRedirectUrl = isPublicProfileFlow
    ? UrlUtils.emailConfirmationRedirectUrlForPublicProfile()
    : undefined

  const onCloseModal = () => {
    commitPendingProfileId()
    closeModal()
  }

  const onPublicProfileLoginSuccess = useCallback(() => {
    commitPendingProfileId()
    closeModal()
    if (!isMountedRef.current) return
    const target = `${FRONTEND_CREATE_PROFILE}/profile`
    if (closeable) {
      history.push(target)
    } else {
      history.replace(target)
    }
  }, [closeable, closeModal, commitPendingProfileId, history])

  const handleSignupCompleteDismiss = () => {
    commitPendingProfileId()
    if (closeable) {
      closeModal()
    } else {
      history.replace(FRONTEND_NEWSFEED_PATH)
    }
  }

  const onRegistrationNext = (registeredEmail: string) => {
    setEmail(registeredEmail)
    setActiveView('email-verification')
  }

  return (
    <AuthenticationModal onClose={wrappedCloseModal}>
      {activeView === 'initial' && (
        <InitialAuthUI
          closeModal={wrappedCloseModal}
          closeable={closeable}
          mode={mode}
          initialTab={returnToLogin ? 'login' : initialTab}
          initialLoginEmail={loginEmail}
          focusTabOnMount={returnToLogin}
          // Leaving this view for a registration drops the forced tab: without it the
          // back arrow out of the registration form would land on login again, carrying
          // an address the user has since changed.
          onSelectPrivateProfile={() => {
            setReturnToLogin(false)
            setLoginEmail('')
            setActiveView('registration-form')
          }}
          onSelectPublicProfile={() => {
            setReturnToLogin(false)
            setLoginEmail('')
            setIsPublicProfileFlow(true)
            setActiveView('public-profile-info')
          }}
          onForgotPassword={() => setActiveView('forgot-password')}
          onRegistrationNext={onRegistrationNext}
        />
      )}
      {activeView === 'registration-form' && (
        <RegistrationForm
          onBack={() =>
            setActiveView(
              isPublicProfileFlow ? 'public-profile-info' : 'initial'
            )
          }
          onNext={onRegistrationNext}
          emailRedirectUrl={publicProfileEmailRedirectUrl}
          onGoToLogin={(email) => {
            setLoginEmail(email)
            setReturnToLogin(true)
            setActiveView('initial')
          }}
        />
      )}
      {activeView === 'email-verification' && (
        <EmailVerification
          email={email}
          onNext={() => setActiveView('phone-verification')}
          emailRedirectUrl={publicProfileEmailRedirectUrl}
        />
      )}
      {activeView === 'phone-verification' && (
        <PhoneVerification
          onNext={(phone) => {
            setPhoneNumber(phone)
            setActiveView('phone-code-verification')
          }}
        />
      )}
      {activeView === 'phone-code-verification' && (
        <PhoneCodeVerification
          phoneNumber={phoneNumber}
          onBack={() => setActiveView('phone-verification')}
          onComplete={(profileId) => {
            storePendingProfileId(profileId)
            setActiveView('signup-complete')
          }}
        />
      )}
      {activeView === 'forgot-password' && (
        <ForgotPasswordForm
          onBack={() => setActiveView('initial')}
          onClose={wrappedCloseModal}
        />
      )}
      {activeView === 'reset-password' && (
        <ResetPasswordForm
          token={resetToken ?? ''}
          email={resetEmail ?? ''}
          onGoToForgotPassword={() => setActiveView('forgot-password')}
          onGoToLogin={() => setActiveView('initial')}
        />
      )}
      {activeView === 'signup-complete' && (
        <SignupComplete
          closeModal={handleSignupCompleteDismiss}
          isPublicProfileFlow={isPublicProfileFlow}
          onCloseModal={onCloseModal}
        />
      )}
      {activeView === 'public-profile-info' && (
        <PublicProfileInfo
          onBack={() => {
            setIsPublicProfileFlow(false)
            setActiveView('initial')
          }}
          onSelectLogin={() => setActiveView('public-profile-login')}
          onSelectRegister={() => setActiveView('registration-form')}
          closeModal={wrappedCloseModal}
        />
      )}
      {activeView === 'public-profile-login' && (
        <div>
          <div className={`${CLASS_PREFIX}public-profile-login__header`}>
            <button onClick={() => setActiveView('public-profile-info')}>
              <SvgIcon name="Arrows/chevron-left" /> {textResources.shared.back}
            </button>
          </div>
          <LoginForm
            closeModal={wrappedCloseModal}
            onForgotPassword={() => setActiveView('forgot-password')}
            onLoginSuccess={onPublicProfileLoginSuccess}
          />
        </div>
      )}
    </AuthenticationModal>
  )
}
